/*
 * SIGNET — renonciation au droit de rétractation sur contenu numérique.
 *
 * Ce que fait ce script
 * ---------------------
 *   1. Page panier : place la case de renonciation juste au-dessus du bouton
 *      de paiement, bloque ce bouton tant qu'elle n'est pas cochée, et écrit
 *      le consentement dans un attribut de panier — repris sur la commande,
 *      donc consultable dans l'admin et imprimable dans les e-mails.
 *   2. Tiroir panier : renvoie vers la page panier tant que le consentement
 *      n'a pas été recueilli, puisque le tiroir n'affiche pas la case.
 *   3. Fiche produit : retire le bouton de paiement accéléré, qui saute le
 *      panier et ne laisse aucun endroit où recueillir la renonciation.
 *
 * Règle de défaillance assumée
 * ----------------------------
 * Si ce script ne tourne pas, rien n'est bloqué et aucun attribut n'est écrit.
 * Dans ce cas la renonciation n'a PAS été recueillie et le délai de quatorze
 * jours s'applique pleinement. C'est le repli sûr : on ne bloque jamais une
 * vente sur une panne de JavaScript, et on ne prétend jamais détenir un
 * consentement qu'on n'a pas.
 *
 * Le bouton n'est jamais mis en `disabled` : un bouton désactivé n'émet plus
 * d'événement de clic, donc plus aucun message d'erreur. On intercepte le clic
 * en phase de capture et on porte `aria-disabled`, ce qui garde le bouton
 * focalisable et le message lisible par un lecteur d'écran.
 */

const CONFIG = readConfig();

function readConfig() {
  const node = document.getElementById('signet-retract-config');
  const fallback = {
    attribute: 'Renonciation au droit de rétractation',
    given: false,
    cartEmpty: true,
    cartUrl: '/cart',
    updateUrl: '/cart/update.js',
    dropAccelerated: false,
    drawerNotice: '',
  };
  if (!node) return fallback;
  try {
    return Object.assign(fallback, JSON.parse(node.textContent));
  } catch (error) {
    return fallback;
  }
}

/* L'état vit en mémoire une fois la page chargée : Horizon re-rend le panier
   par morceaux, et relire l'attribut à chaque re-rendu coûterait un aller-retour
   réseau pour une valeur qu'on vient nous-même d'écrire. */
let consentGiven = CONFIG.given === true;
let pending = false;

/* ------------------------------------------------------------------ */
/* 1. Fiche produit — retrait du paiement accéléré                      */
/* ------------------------------------------------------------------ */

function dropAccelerated(root) {
  if (!CONFIG.dropAccelerated) return;
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('.accelerated-checkout-block').forEach((node) => {
    node.remove();
  });
}

/* ------------------------------------------------------------------ */
/* 2. Écriture du consentement                                          */
/* ------------------------------------------------------------------ */

function record(checked, statement, widget) {
  const value = checked
    ? ['Oui', new Date().toISOString(), statement].filter(Boolean).join(' — ')
    : '';

  pending = true;
  syncCartPage();

  return fetch(CONFIG.updateUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [CONFIG.attribute]: value } }),
  })
    .then((response) => {
      if (!response.ok) throw new Error('cart update refused');
      /* Le consentement n'est acquis qu'une fois le panier effectivement mis à
         jour. Une case cochée dont l'écriture a échoué ne vaut rien : elle
         laisserait passer une commande sans la moindre trace du consentement. */
      consentGiven = checked;
      return true;
    })
    .catch(() => {
      /* Écriture refusée : on remet la case dans son état réel plutôt que de
         laisser croire au visiteur que c'est enregistré. */
      consentGiven = false;
      const box = widget && widget.querySelector('[data-signet-retract-box]');
      if (box) box.checked = false;
      if (widget) showError(widget, true);
      return false;
    })
    .finally(() => {
      pending = false;
      syncCartPage();
    });
}

/* ------------------------------------------------------------------ */
/* 3. Page panier                                                       */
/* ------------------------------------------------------------------ */

function moveIntoSummary(widget) {
  /* Horizon rend les blocs de section dans `.cart-page__more-blocks`, après le
     récapitulatif. Un consentement doit se lire avant le bouton qu'il commande,
     pas après : on le déplace juste au-dessus. */
  const ctas = document.querySelector('.cart-summary .cart__ctas');
  if (!ctas || !ctas.parentNode) return;
  if (widget.parentNode === ctas.parentNode && widget.nextElementSibling === ctas) return;
  ctas.parentNode.insertBefore(widget, ctas);
  widget.dataset.signetPlaced = 'true';
}

function checkoutButtons() {
  return Array.from(document.querySelectorAll('.cart__checkout-button, button[name="checkout"]'));
}

function showError(widget, show) {
  const error = widget.querySelector('[data-signet-retract-error]');
  if (!error) return;
  error.hidden = !show;
  widget.classList.toggle('signet-retract--pending', show);
}

function syncCartPage() {
  const widget = document.querySelector('[data-signet-retract]');
  const buttons = checkoutButtons();

  if (!widget) {
    /* Pas de case sur cette page : on lève tout blocage qu'on aurait posé,
       sans toucher à un bouton désactivé par Horizon lui-même. */
    buttons.forEach(release);
    return;
  }

  /* Horizon capture les blocs de section deux fois : une fois dans le DOM
     vivant, une fois dans `<template id="empty-cart-template">`. Le contenu
     d'un `<template>` est inerte et hors d'atteinte de `querySelector`, donc
     ça ne gêne pas — sauf au moment où le visiteur retire sa dernière ligne :
     Horizon injecte alors ce template, rendu quand le panier n'était pas
     encore vide, et la case réapparaît sur un panier vide. On la retire. */
  if (widget.closest('.cart-page--empty')) {
    widget.remove();
    buttons.forEach(release);
    return;
  }

  moveIntoSummary(widget);
  bind(widget);

  /* Seul le consentement confirmé par le serveur ouvre le bouton. Une case
     cochée dont l'écriture est encore en vol ne suffit pas. */
  const allowed = consentGiven && !pending;

  buttons.forEach((button) => {
    if (allowed) release(button);
    else hold(button);
  });

  if (allowed) showError(widget, false);
}

function hold(button) {
  if (button.disabled) return; // désactivé par Horizon (panier vide) : on n'y touche pas
  button.dataset.signetBlocked = 'true';
  button.setAttribute('aria-disabled', 'true');
}

function release(button) {
  if (!button.dataset.signetBlocked) return;
  delete button.dataset.signetBlocked;
  button.removeAttribute('aria-disabled');
}

function bind(widget) {
  if (widget.dataset.signetBound === 'true') return;
  widget.dataset.signetBound = 'true';

  const box = widget.querySelector('[data-signet-retract-box]');
  if (!box) return;

  const statement = widget.dataset.statement || '';
  if (box.checked) consentGiven = true;

  box.addEventListener('change', () => {
    showError(widget, false);
    if (!box.checked) consentGiven = false;
    record(box.checked, statement, widget);
  });
}

/* Interception en phase de capture : le clic est arrêté avant que le
   formulaire du panier ne le voie. */
document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest && event.target.closest('button[name="checkout"], .cart__checkout-button');
    if (!button || !button.dataset.signetBlocked) return;

    event.preventDefault();
    event.stopPropagation();

    const widget = document.querySelector('[data-signet-retract]');
    if (!widget) return;
    showError(widget, true);
    const box = widget.querySelector('[data-signet-retract-box]');
    if (box) box.focus({ preventScroll: false });
  },
  true
);

/* ------------------------------------------------------------------ */
/* 4. Tiroir panier                                                     */
/* ------------------------------------------------------------------ */

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest && event.target.closest('button[name="checkout"], .cart__checkout-button');
    if (!button) return;
    if (consentGiven) return;
    if (button.disabled) return;

    /* Le bouton de la page panier est déjà traité par le blocage ci-dessus :
       ici on ne s'occupe que de celui du tiroir, qui n'affiche pas la case. */
    const inDrawer = button.closest('cart-drawer-component, .cart-drawer, dialog');
    if (!inDrawer) return;
    if (document.querySelector('[data-signet-retract]')) return;

    event.preventDefault();
    event.stopPropagation();
    window.location.href = CONFIG.cartUrl;
  },
  true
);

/* ------------------------------------------------------------------ */
/* 5. Re-rendus d'Horizon                                               */
/* ------------------------------------------------------------------ */

function boot() {
  dropAccelerated(document);
  syncCartPage();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

/* Horizon remplace des morceaux de DOM après chaque mise à jour du panier
   (quantité, code promo, tiroir). Un observateur débrayé d'une frame suffit :
   `syncCartPage` est idempotent et ne fait aucune requête. */
let scheduled = false;
const observer = new MutationObserver(() => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    dropAccelerated(document);
    syncCartPage();
  });
});

observer.observe(document.documentElement, { childList: true, subtree: true });

window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
